import { Component } from '@theme/component';

/**
 * <q-reviews> — the store's own reviews panel (snippets/custom-reviews.liquid).
 *
 * The page works without this module (every review listed, the form posts
 * normally). It adds what shoppers expect from a review section:
 *   • star-breakdown rows filter the list (tap again to clear)
 *   • sort: most recent / highest / lowest
 *   • paging: `data-page-size` reviews at a time, "Show more" for the rest
 *   • the "Write a review" form opens in place, is checked before sending
 *     (a rating is required, fields are focused on the first problem) and
 *     can only be sent once per click
 *   • the email the store receives carries a ready-to-paste line in the
 *     publish format (snippets/custom-product-reviews.liquid)
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [write] - only rendered for verified buyers
 * @property {HTMLElement} [form]
 * @property {HTMLInputElement} [line]
 * @property {HTMLElement} [ratingField]
 * @property {HTMLElement} [ratingError]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLUListElement} [list]
 * @property {HTMLSelectElement} [sort]
 * @property {HTMLButtonElement} [more]
 * @property {HTMLButtonElement[]} [bars]
 * @extends {Component<Refs>}
 */
class Reviews extends Component {
  /** Star filter: 0 = all */
  #stars = 0;
  #shown = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#shown = this.#pageSize;
    this.#render();
    const form = this.querySelector('form');
    if (form) {
      // Checked here instead (rating first, then the browser's own messages)
      form.noValidate = true;
      // On the button's click, not on submit: Shopify's spam check (hCaptcha)
      // starts on the submit event and would challenge a form that is not
      // ready to send. Enter in a field also "clicks" the submit button.
      this.refs.submit?.addEventListener('click', this.#onSubmitClick);
    }
    // The form was sent, or came back with errors: nothing left to "open"
    if (this.refs.form) this.#setExpanded(!this.refs.form.hidden);
    else if (this.refs.write) this.refs.write.hidden = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.submit?.removeEventListener('click', this.#onSubmitClick);
  }

  get #pageSize() {
    return Number(this.dataset.pageSize) || 5;
  }

  /** @returns {HTMLLIElement[]} */
  get #items() {
    return this.refs.list ? /** @type {HTMLLIElement[]} */ ([...this.refs.list.children]) : [];
  }

  /**
   * Star-breakdown row: show only reviews with that rating; again to clear.
   * @param {number | string} stars - inline data of on:click="/filter/N"
   */
  filter(stars) {
    const value = Number(stars);
    this.#stars = this.#stars === value ? 0 : value;
    this.#shown = this.#pageSize;
    for (const bar of this.refs.bars ?? []) {
      bar.setAttribute('aria-pressed', String(Number(bar.dataset.stars) === this.#stars));
    }
    this.#render();
  }

  sort() {
    const { list, sort } = this.refs;
    if (!list || !sort) return;
    const order = sort.value;
    /** @param {HTMLLIElement} li */
    const rating = (li) => Number(li.dataset.rating);
    /** @param {HTMLLIElement} li */
    const index = (li) => Number(li.dataset.index);

    // data-index is the server's order: newest first (also the tie-breaker)
    const sorted = this.#items.sort((a, b) => {
      if (order === 'highest') return rating(b) - rating(a) || index(a) - index(b);
      if (order === 'lowest') return rating(a) - rating(b) || index(a) - index(b);
      return index(a) - index(b);
    });
    list.append(...sorted);
    this.#shown = this.#pageSize;
    this.#render();
  }

  showMore() {
    const firstNew = this.#matching[this.#shown];
    this.#shown += this.#pageSize;
    this.#render();
    // Keyboard and screen-reader users continue from the first new review
    if (firstNew) {
      firstNew.tabIndex = -1;
      firstNew.focus({ preventScroll: true });
    }
  }

  get #matching() {
    return this.#items.filter((li) => !this.#stars || Number(li.dataset.rating) === this.#stars);
  }

  /** Applies filter + paging to the list */
  #render() {
    const matching = new Set(this.#matching);
    let visible = 0;
    for (const li of this.#items) {
      const show = matching.has(li) && visible < this.#shown;
      li.hidden = !show;
      if (show) visible++;
    }
    if (this.refs.more) this.refs.more.hidden = matching.size <= this.#shown;
  }

  /** "Write a review" / "Cancel" */
  toggleForm() {
    const { form } = this.refs;
    if (!form) return;
    const open = form.hidden;
    form.hidden = !open;
    this.#setExpanded(open);
    if (open) {
      form.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="radio"]'))?.focus({ preventScroll: true });
    } else {
      this.refs.write?.focus();
    }
  }

  /** @param {boolean} open */
  #setExpanded(open) {
    this.refs.write?.setAttribute('aria-expanded', String(open));
  }

  clearError() {
    if (this.refs.ratingError) this.refs.ratingError.hidden = true;
  }

  /** @param {MouseEvent} event */
  #onSubmitClick = (event) => {
    const { submit, line, ratingError, ratingField } = this.refs;
    const form = submit?.form;
    if (!form) return;

    // Already on its way (double click / double tap)
    if (submit?.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }

    const data = new FormData(form);
    const rating = String(data.get('contact[Rating]') ?? '');
    if (!rating) {
      event.preventDefault();
      if (ratingError) ratingError.hidden = false;
      /** @type {HTMLInputElement | null | undefined} */ (ratingField?.querySelector('input'))?.focus();
      return;
    }

    // Name, email, review: the browser's own messages, on the first bad field
    for (const field of form.querySelectorAll('input:not([type="hidden"]):not([type="radio"]), textarea')) {
      const input = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (field);
      input.value = input.value.trim();
      const valid = input.checkValidity();
      input.setAttribute('aria-invalid', String(!valid));
      if (!valid) {
        event.preventDefault();
        input.reportValidity();
        return;
      }
    }

    if (line) {
      // "5 | Aisha M. | 2026-09-28 | text": one line, no stray separators
      const clean = (/** @type {FormDataEntryValue | null} */ value) =>
        String(value ?? '').replace(/[|\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
      const today = new Date().toISOString().slice(0, 10);
      line.value = [rating, clean(data.get('contact[name]')), today, clean(data.get('contact[body]'))].join(' | ');
    }

    submit?.setAttribute('aria-busy', 'true');
    // The page normally reloads with the result. If the spam check asks the
    // shopper a question and they close it, the button must work again.
    setTimeout(() => submit?.removeAttribute('aria-busy'), 8000);
  };
}

if (!customElements.get('q-reviews')) {
  customElements.define('q-reviews', Reviews);
}

// Back/forward cache: a restored page must not keep the "sending" lock
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  for (const button of document.querySelectorAll('q-reviews [aria-busy="true"]')) button.removeAttribute('aria-busy');
});
